import axios from 'axios';
import cx from 'classnames';
import { useMergeVariables } from 'data/touch-trigger';
import { Field, Form, Formik } from 'formik';
import React from 'react';
import { toast } from 'sonner';
import { EAIPromptType } from 'types/ai-generation';
import { ETouchType } from 'types/touch-type';
import * as Yup from 'yup';

import {
  Box, ButtonBase, Chip, Tooltip,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';

import Button from 'creative-components/CustomButtons/Button';
import CustomInput from 'creative-components/CustomInput/CustomInput';

import MagicPenIcon from 'components/CustomIcons/MagicPenIcon';
import LoadingIndicator from 'components/LoadingIndicator/LoadingIndicator';
import { useLoadingIndicatorContext } from 'components/LoadingIndicator/LoadingIndicatorProvider';

import { createCustomTouchTemplate } from 'utils/api';
import { submitTouchTriggerAIGeneration } from 'utils/api/ai';
import { showAPIErrorAlert } from 'utils/lib';

const useStyles = makeStyles((theme) => ({
  tabs: {
    margin: '40px 0 0',
  },
  tabsHidden: {
    display: 'none',
  },
  instructions: {
    color: theme.palette.darkGray.main,
    fontFamily: 'Montserrat',
    fontWeight: 500,
    margin: '30px 0 30px',

    '& > span': {
      color: theme.palette.lightGray.main,
    },
  },
  formControl: {
    '& > div:first-child': {
      fontWeight: 500,
    },
  },
  customBodyTextInput: {
    '& textarea > span': {
      color: 'red',
    },
  },
  aiBox: {
    marginTop: '4px',
    padding: '14px 8px',
    border: `2px solid ${theme.palette.white.main}`,
    borderRadius: '5px',
  },
  aiBoxFocused: {
    border: `2px solid ${theme.palette.primary.light}`,
  },
  aiTextButton: {
    fontWeight: 600,
    fontSize: '14px',
    color: theme.palette.primary.main,
    background: theme.palette.white.main,
    padding: '3px 7px',
    border: `1px solid ${theme.palette.grayScale5.main}`,
    borderRadius: '5px',

    '& > svg': {
      fontSize: '20px',
      marginRight: '6px',
    },
  },
  aiTextButtonSelected: {
    border: `3px solid ${theme.palette.primary.light}`,
  },
  aiButton: {
    borderRadius: '8px',
    padding: '8px 12px',
    fontWeight: 600,
    fontSize: '12px',
    lineHeight: '16px',
    textTransform: 'uppercase',
  },
  aiButtonCancel: {
    backgroundColor: theme.palette.grayScale6.main,
    color: theme.palette.grayScale9.main,
  },
  aiButtonGenerate: {
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.grayScale9.main,
  },
  flash: {
    animation: '$flashHighlight 3000ms ease-in-out',
  },
  '@keyframes flashHighlight': {
    '0%': { backgroundColor: '#fff3cd' }, // Light yellow
    '40%': { backgroundColor: '#fff3cd' }, // Light yellow
    '100%': { backgroundColor: 'transparent' },
  },
  availableMergeVariables: {
    '& > span': {
      margin: '30px 0 12px',
      display: 'flex',
      alignItems: 'center',
    },
    '& h4': { // Title
      display: 'inline-block',
      color: theme.palette.darkGray.main,
      fontFamily: 'Montserrat',
      fontWeight: 500,
      marginRight: '10px',
    },
    '& > div': {
      display: 'flex',
      flexDirection: 'row',
      flexWrap: 'wrap',
    },
  },
  mergeVariableChip: {
    margin: '5px',
    fontWeight: 400,
  },
  actions: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    marginTop: '30px',

    '& > div': {
      marginTop: '10px', // For better rendering when wrapping
    },
    '& > div > button:first-child': {
      marginRight: '20px',
    },
  },
}));

const aiTextPromptMinLength = 20;
const aiTextPromptMaxLength = 400;

const EditBodyText = ({
  onClose, touchTrigger, onReceiveEditedTouchTrigger, setIsConfirmingReset,
}) => {
  const classes = useStyles();
  const { showLoadingIndicatorModal, hideLoadingIndicatorModal } = useLoadingIndicatorContext();

  const { customTouchTemplate, touchType: { name: touchTypeName } } = touchTrigger;

  const existingCustomBodyText = customTouchTemplate && customTouchTemplate.extraMergeVariables.customBodyText;
  const existingCustomEmailSubject = customTouchTemplate && customTouchTemplate.extraMergeVariables.customEmailSubject;

  const { mergeVariables, isLoadingMergeVariables } = useMergeVariables(touchTrigger._id);

  const [defaultCustomBodyText, setDefaultCustomBodyText] = React.useState(undefined);

  const [aiPromptType, setAIPromptType] = React.useState(null);
  const [aiTextPrompt, setAiTextPrompt] = React.useState('');
  const [lastSubmittedAITextPrompt, setLastSubmittedAITextPrompt] = React.useState(''); // Used to hide the generate button
  // Used to flash fields after it fills in from AI generation
  const [flashAIUpdatedFields, setFlashAIUpdatedFields] = React.useState(false);

  React.useEffect(() => {
    (async () => {
      if (touchTrigger.metadata.defaultCustomBodyTextUrl) {
        const { data } = await axios.get(touchTrigger.metadata.defaultCustomBodyTextUrl);
        setDefaultCustomBodyText(data);
      } else {
        setDefaultCustomBodyText(null);
      }
    })();
  }, []);

  React.useEffect(() => {
    if (flashAIUpdatedFields) {
      const timeout = setTimeout(() => setFlashAIUpdatedFields(false), 3000); // Use same ms as in animation CSS
      return () => clearTimeout(timeout);
    }

    return () => {};
  }, [flashAIUpdatedFields]);

  if (isLoadingMergeVariables || defaultCustomBodyText === undefined) {
    return (
      <div style={{ marginTop: '40px' }}>
        <LoadingIndicator />
      </div>
    );
  }

  return (
    <Formik
      validateOnChange={false}
      initialValues={{
        customEmailSubject: existingCustomEmailSubject || touchTrigger.metadata.defaultSubject,
        customBodyText: existingCustomBodyText || defaultCustomBodyText || '',
      }}
      onSubmit={async ({ customBodyText, customEmailSubject }) => {
        // Show a loading indicator during the upload
        showLoadingIndicatorModal();

        try {
          const { touchTrigger: updatedTouchTrigger } = await createCustomTouchTemplate(
            touchTrigger._id,
            undefined,
            customBodyText,
            undefined,
            [ETouchType.Email, ETouchType.Voicemail].includes(touchTypeName) ? customEmailSubject : undefined,
          );

          // Callback to update the touch trigger locally
          onReceiveEditedTouchTrigger(updatedTouchTrigger);

          onClose();

          toast.success('Your edits have been saved and will be used when sending out your touch! Please preview your touch to make sure everything looks great!');
        } catch (err) {
          console.error(err);
          showAPIErrorAlert(err);
        }

        hideLoadingIndicatorModal();
      }}
      validationSchema={Yup.object().shape({
        customEmailSubject: Yup.string(),
        customBodyText: Yup.string().required(),
        // customBodyText: Yup.string().required().max(1000),
      })}
    >
      {(props) => {
        const {
          touched, errors, dirty, isValid, handleSubmit, isSubmitting, setFieldValue, values,
        } = props;

        const onAICancel = () => {
          setAIPromptType(null);
          setAiTextPrompt('');
        };

        const onAIGenerate = async () => {
          showLoadingIndicatorModal();

          try {
            if (aiTextPrompt.length > aiTextPromptMaxLength) {
              toast.warning(`Your prompt is too long, it must be at most ${aiTextPromptMaxLength} characters.`);
              return;
            }
            if (aiTextPrompt.length < aiTextPromptMinLength) {
              toast.warning(`Your prompt is too short, it must be at least ${aiTextPromptMinLength} characters.`);
              return;
            }

            const res = await submitTouchTriggerAIGeneration(
              touchTrigger._id,
              {
                promptType: aiPromptType,
                prompt: aiTextPrompt,
                existingBodyText: [EAIPromptType.EmailVariation, EAIPromptType.LetterVariation].includes(aiPromptType) ? values.customBodyText : undefined,
              },
            );

            setLastSubmittedAITextPrompt(aiTextPrompt); // Used to hide the generate button

            // Output text is formatted as JSON and has a "body" key. Emails also have a "subject" key.
            const { subject, body } = JSON.parse(res.outputText);

            if (touchTypeName === ETouchType.Email) {
              setFieldValue('customEmailSubject', subject, true);
            }
            setFieldValue('customBodyText', body, true);

            setFlashAIUpdatedFields(true);
            toast.success('The AI-generated text has been autofilled for you. If you like it, remember to save the changes and preview your touch!');
          } catch (err) {
            console.error('error', err);
            showAPIErrorAlert(err);
          } finally {
            hideLoadingIndicatorModal();
          }
        };

        return (
          <Form>
            {[ETouchType.Email, ETouchType.Voicemail].includes(touchTypeName) && (
              <Field
                name="customEmailSubject"
              >
                {({ field }) => (
                  <CustomInput
                    labelText="Email Subject"
                    formControlProps={{
                      classes: {
                        root: classes.formControl,
                      },
                    }}
                    inputProps={{
                      ...field,
                      className: cx({ [classes.flash]: flashAIUpdatedFields }),
                    }}
                    error={touched[field.name] && errors[field.name] !== undefined}
                  />
                )}
              </Field>
            )}

            <Field
              name="customBodyText"
            >
              {({ field }) => (
                <CustomInput
                  labelText="Body Text"
                  formControlProps={{
                    classes: {
                      root: classes.formControl,
                    },
                    className: classes.customBodyTextInput,
                    fullWidth: true,
                    style: { paddingTop: 0 },
                  }}
                  inputProps={{
                    ...field,
                    multiline: true,
                    rows: 8,
                    style: {
                      resize: 'vertical',
                    },
                    className: cx({ [classes.flash]: flashAIUpdatedFields }),
                  }}
                  error={touched[field.name] && errors[field.name] !== undefined}
                />
              )}
            </Field>

            <Box className={cx(classes.aiBox, !!aiPromptType && classes.aiBoxFocused)}>
              <Box display="flex" style={{ gap: '16px' }}>
                <ButtonBase
                  className={cx(classes.aiTextButton, [EAIPromptType.EmailGeneration, EAIPromptType.LetterGeneration].includes(aiPromptType) && classes.aiTextButtonSelected)}
                  onClick={() => (touchTypeName === ETouchType.Letter ? setAIPromptType(EAIPromptType.LetterGeneration) : setAIPromptType(EAIPromptType.EmailGeneration))}
                >
                  <MagicPenIcon />
                  Help me write with AI
                </ButtonBase>
                <ButtonBase
                  className={cx(classes.aiTextButton, [EAIPromptType.EmailVariation, EAIPromptType.LetterVariation].includes(aiPromptType) && classes.aiTextButtonSelected)}
                  onClick={() => (touchTypeName === ETouchType.Letter ? setAIPromptType(EAIPromptType.LetterVariation) : setAIPromptType(EAIPromptType.EmailVariation))}
                >
                  <MagicPenIcon />
                  Generate a variation with AI
                </ButtonBase>
              </Box>

              {!!aiPromptType && (
                <>
                  <Box marginTop={2}>
                    <CustomInput
                      formControlProps={{
                        style: { marginBottom: 0 },
                      }}
                      inputProps={{
                        placeholder: [EAIPromptType.EmailVariation, EAIPromptType.LetterVariation].includes(aiPromptType)
                          ? 'Write your prompt here. For example, "Make this more professional" or "Make this more friendly and include information about recent interest rate changes".'
                          : 'Write your prompt here.',
                        value: aiTextPrompt,
                        onChange: (e) => setAiTextPrompt(e.target.value),
                        multiline: true,
                        rows: 4,
                        style: {
                          resize: 'vertical',
                        },
                      }}
                    // error={touched[field.name] && errors[field.name] !== undefined}
                      error={aiTextPrompt.length > aiTextPromptMaxLength}
                      helperText={`${aiTextPrompt.length} / ${aiTextPromptMaxLength} characters`}
                    />
                  </Box>

                  <Box marginTop={2} display="flex" justifyContent="space-between">
                    <Button
                      className={cx(classes.aiButton, classes.aiButtonCancel)}
                      onClick={onAICancel}
                    >
                      Cancel
                    </Button>
                    {aiTextPrompt !== lastSubmittedAITextPrompt && (
                      <Button
                        className={cx(classes.aiButton, classes.aiButtonGenerate)}
                        onClick={onAIGenerate}
                      >
                        Generate
                      </Button>
                    )}
                  </Box>
                </>
              )}
            </Box>

            <div className={classes.availableMergeVariables}>
              <span>
                <h4>Available Custom Variables</h4>
                <Tooltip
                  placement="top"
                  disableFocusListener
                  disableTouchListener
                  title="These special merge tags will be substituted with information when your touch goes out. You can use them as many times as you want. Use them and preview your touch to see them in action!"
                >
                  <InfoOutlinedIcon />
                </Tooltip>
              </span>
              <div>
                {mergeVariables.map((v) => (
                  <Chip
                    key={v}
                    color={values.customEmailSubject?.match(new RegExp(`(\\b)${v}(\\b)`, 'g')) || values.customBodyText.match(new RegExp(`(\\b)${v}(\\b)`, 'g')) ? 'primary' : 'default'}
                    label={v}
                    className={classes.mergeVariableChip}
                  />
                ))}
              </div>
            </div>

            <div className={classes.actions}>
              {/* Span for buttons to go to the right */}
              <span />

              <div>
                {customTouchTemplate && (
                  <Button
                    color="white"
                    round
                    onClick={() => setIsConfirmingReset(true)}
                  >
                    Reset
                  </Button>
                )}
                <Button
                  color="primary"
                  round
                  disabled={isSubmitting}
                  onClick={handleSubmit}
                >
                  Save
                </Button>
              </div>
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};

export default EditBodyText;
